import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
// Used by the tracking detail drawer below. It was referenced without ever
// being imported, which threw "WorkerProfileDetailedSection is not defined" and
// blanked the whole drawer the moment a worker row was opened.
import WorkerProfileDetailedSection from "../workerDetailedProfile/WorkerProfileDetailedSection.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useLatestTrackingList, LATEST_TRACKING_DEFAULT_PAGE_SIZE } from "./useLatestTrackingList.js";
import { getLatestTrackingDetail } from "./trackingApi.js";
import "./latestTracking.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Tracking", "Latest Status"];
const DRAWER_TITLE_ID = "trk-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const HEARTBEAT_STATE_OPTIONS = ["Online", "Warning", "Missing", "NeverSeen"];
const OVERALL_STATUS_OPTIONS = ["Unknown"];

const HERO_COPY_TEXT = "ดูสถานะ heartbeat และตำแหน่งล่าสุดของแรงงาน ยังไม่ใช่ Footstep History หรือ Geofence Evaluation";
const TOP_WARNING_TITLE = "หน้านี้แสดงเฉพาะสถานะล่าสุด ไม่ใช่ประวัติการเดินทาง";
const TOP_WARNING_BODY =
  "หน้านี้บอกได้เพียงว่าแรงงานอยู่ที่ไหนล่าสุด ไม่ใช่เส้นทางที่เดินมา และไม่ได้ตรวจโซนหรือการปลอมพิกัดให้";
const FOOTSTEP_RESERVED_REASON = "ยังไม่เปิดใช้งาน";
const FOOTSTEP_CROSSLINK_TEXT = "เปิด Footstep History";
const FOOTSTEP_CROSSLINK_NOTE =
  "ลิงก์นี้เปิดหน้า Footstep History แบบทั่วไป (ไม่ส่ง Worker/ช่วงเวลาล่วงหน้า) — ต้องเลือกแรงงานและระบุเหตุผล (Purpose) ก่อนเปิดดูประวัติเสมอ ทุกการเปิดดูจะถูกบันทึก audit log";
const MISSING_HEARTBEAT_TITLE = "Heartbeat หายเกิน threshold";
const MISSING_HEARTBEAT_NOTE = "หน้านี้ไม่ได้สร้างรายการผิดปกติให้อัตโนมัติ หากพบแรงงานขาดสัญญาณ ต้องแจ้งต่อเอง";
const FOOTNOTE_TEXT =
  "พิกัดแสดงเป็นตัวเลขในลิ้นชักรายละเอียดเท่านั้น หน้านี้ยังไม่มีแผนที่";

const HEARTBEAT_STATE_BADGE_VARIANT = { Online: "success", Warning: "warning", Missing: "danger", NeverSeen: "neutral" };
const HEARTBEAT_STATE_LABEL_TH = { Online: "Online", Warning: "Warning", Missing: "Missing", NeverSeen: "Never Seen" };

function heartbeatBadgeVariant(state) {
  return HEARTBEAT_STATE_BADGE_VARIANT[state] ?? "neutral";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการสถานะติดตามนี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูสถานะติดตามของแรงงานรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบข้อมูลสถานะติดตามของแรงงานรายนี้";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

/** Two-letter monogram from a worker's name - never a fabricated icon/logo. */
function monogram(name) {
  const trimmed = (name ?? "").trim();
  if (!trimmed) {
    return "-";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

/**
 * /backoffice/tracking/latest - the Backoffice Latest Tracking screen
 * (Prompt 113B, built from the W54 reference). Entirely read-only over the
 * Prompt 112 Backoffice Latest Tracking API: a paginated, scope-isolated
 * list of every visible worker's latest heartbeat/location snapshot
 * (GET .../tracking/workers/latest), plus a single-worker detail
 * (GET .../tracking/workers/{workerId}/latest). This screen never calls a
 * mobile ingestion/geofence/fake-location/face-recognition/SOS API (none of
 * those exist anywhere in this codebase), never renders a map, and
 * canViewFootstepHistory is always rendered blocked with the fixed
 * reserved-reason string, matching TrackingNextActionsDto's own
 * always-false contract.
 *
 * The sidebar's "Footstep" item (../shell/navConfig.js) now links here -
 * the existing IA has no dedicated "Tracking Monitor" item yet, so this
 * screen reuses the nearest existing Command-group item while this page's
 * own title ("Latest Tracking Status", never "Footstep") and permanent top
 * banner make it unambiguous that this is latest-status only, not Footstep
 * history.
 */
export default function LatestTrackingPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [employerIdInput, setEmployerIdInput] = useState("");
  const [overallStatusFilter, setOverallStatusFilter] = useState("");
  const [heartbeatStateFilter, setHeartbeatStateFilter] = useState("");
  const [page, setPage] = useState(1);
  // Clicking the row opens the ONE shared worker record; the row's own
  // action button keeps opening this screen's process detail.
  const [workerRecordId, setWorkerRecordId] = useState(null);

  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, overallStatusFilter, heartbeatStateFilter]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: LATEST_TRACKING_DEFAULT_PAGE_SIZE,
      search,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      employerId: employerIdInput,
      overallStatus: overallStatusFilter,
      heartbeatState: heartbeatStateFilter,
    }),
    [page, search, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, overallStatusFilter, heartbeatStateFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useLatestTrackingList(filters);

  // Detail is fetched whenever selectedWorkerId changes (row/card opened).
  useEffect(() => {
    if (!selectedWorkerId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedWorkerId ? previous.data : null,
      error: null,
      forId: selectedWorkerId,
    }));

    getLatestTrackingDetail(selectedWorkerId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedWorkerId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedWorkerId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedWorkerId
  }, [selectedWorkerId, detailReloadToken]);

  function openWorkerDetail(workerId) {
    setSelectedWorkerId(workerId);
  }

  function closeDrawer() {
    setSelectedWorkerId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedWorkerId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / LATEST_TRACKING_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search || superAgentOrgInput || plcOrgInput || agentIdInput || employerIdInput || overallStatusFilter || heartbeatStateFilter
  );

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="map" label="กำลังโหลดรายการสถานะติดตามล่าสุด..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการสถานะติดตามล่าสุดได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-trk-page">
        <div className="wx-trk-toolbar">
          <label className="wx-trk-search">
            <span className="wx-visually-hidden">ค้นหารหัสแรงงาน ชื่อ Assignment หรือนายจ้าง</span>
            <input
              type="search"
              placeholder="ค้นหารหัสแรงงาน ชื่อ Assignment หรือนายจ้าง"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-trk-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost wx-trk-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
          </div>

          <div className="wx-trk-filters">
            <label className="wx-trk-filter">
              <span>สถานะรวม</span>
              <select value={overallStatusFilter} onChange={(event) => setOverallStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {OVERALL_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-trk-filter">
              <span>Heartbeat State</span>
              <select value={heartbeatStateFilter} onChange={(event) => setHeartbeatStateFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {HEARTBEAT_STATE_OPTIONS.map((state) => (
                  <option key={state} value={state}>
                    {HEARTBEAT_STATE_LABEL_TH[state]}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-trk-filter" title="ตัวกรองนี้ใช้งานได้จริง แต่ยังไม่มีรายชื่อ Super Agent ให้เลือก จึงต้องกรอกรหัสเอง">
              <span>Super Agent Org</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
            </label>

            <label className="wx-trk-filter" title="ตัวกรองนี้ใช้งานได้จริง แต่ยังไม่มีรายชื่อ PLC ให้เลือก จึงต้องกรอกรหัสเอง">
              <span>PLC / บมจ</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
            </label>

            <label className="wx-trk-filter" title="ตัวกรองนี้ใช้งานได้จริง แต่ยังไม่มีรายชื่อ Agent ให้เลือก จึงต้องกรอกรหัสเอง">
              <span>Agent</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={agentIdInput} onChange={(event) => setAgentIdInput(event.target.value)} />
            </label>

            <label className="wx-trk-filter" title="ตัวกรองนี้ใช้งานได้จริง แต่ยังไม่มีรายชื่อนายจ้างให้เลือก จึงต้องกรอกรหัสเอง">
              <span>นายจ้าง</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={employerIdInput} onChange={(event) => setEmployerIdInput(event.target.value)} />
            </label>

          </div>
        </div>

        <header className="wx-trk-head">
          <div className="wx-trk-title">
            <h1>
              Latest Tracking Status <span className="wx-trk-title-th">สถานะติดตามล่าสุด</span>
            </h1>
            <p>{HERO_COPY_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-trk-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-trk-top-notice" role="status">
          <strong>{TOP_WARNING_TITLE}</strong>
          <span>{TOP_WARNING_BODY}</span>
        </div>

        {/* A row of five KPI tiles stood here, every one of them rendering the
            word "สงวนไว้" over a pill reading "รอ Summary API" - a whole band of
            the page, on every visit, reporting that we had not built a summary
            endpoint. No number was ever shown. The list below is the real
            content; it is now the first thing under the header. */}

        <section className="wx-panel wx-trk-list-panel">
          <div className="wx-trk-list-head">
            <h2 className="wx-panel-title">รายการแรงงาน</h2>
            <span className="wx-trk-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-trk-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีข้อมูลสถานะติดตามในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-trk-table-wrap">
                <table className="wx-trk-table">
                  <colgroup>
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>แรงงาน</th>
                      <th>Assignment</th>
                      <th>นายจ้าง</th>
                      <th>Agent</th>
                      <th>Heartbeat ล่าสุด</th>
                      <th>ตำแหน่งล่าสุด</th>
                      <th>อุปกรณ์</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.workerId} className="wx-trk-row" onClick={() => setWorkerRecordId(item.workerId)}>
                        <td>
                          <div className="wx-trk-job-cell">
                            <span className="wx-trk-avatar" aria-hidden="true">
                              {monogram(item.workerName)}
                            </span>
                            <div>
                              <span className="wx-trk-cell-strong">{item.workerName ?? "-"}</span>
                              <span className="wx-trk-cell-sub">{item.workerCode ?? "-"}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="wx-trk-cell-strong wx-trk-code">{item.assignmentCode ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-trk-cell-strong">{item.employerName ?? "-"}</span>
                        </td>
                        <td>
                          <span className="wx-trk-cell-strong">{item.agentName ?? "-"}</span>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${heartbeatBadgeVariant(item.heartbeatState)}`}>
                            {HEARTBEAT_STATE_LABEL_TH[item.heartbeatState] ?? item.heartbeatState}
                          </span>
                          <span className="wx-trk-cell-sub">{formatDateTimeTh(item.lastHeartbeatAtUtc) ?? "ยังไม่เคยส่ง heartbeat"}</span>
                        </td>
                        <td>
                          <span className={item.lastLocationAtUtc ? "wx-trk-cell-strong" : "wx-trk-reserved-text"}>
                            {formatDateTimeTh(item.lastLocationAtUtc) ?? "ยังไม่มีข้อมูลตำแหน่ง"}
                          </span>
                        </td>
                        <td>
                          <span className="wx-trk-cell-strong">
                            {item.batteryLevel != null ? `${item.batteryLevel}%` : "-"} · {item.networkType ?? "ไม่ทราบ"}
                          </span>
                          <span className="wx-trk-cell-sub">
                            {item.appVersion ?? "-"} · {item.deviceBindingStatus ?? "ไม่ระบุ"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="wx-trk-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openWorkerDetail(item.workerId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-trk-cards">
                {items.map((item) => (
                  <WorkerTrackingCard key={item.workerId} item={item} onOpen={() => openWorkerDetail(item.workerId)} />
                ))}
              </div>

              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          )}
        </section>

        <p className="wx-trk-footnote">{FOOTNOTE_TEXT}</p>
      </div>

      <DetailDrawer
        open={selectedWorkerId !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-trk-row, .wx-trk-card, .wx-trk-refresh-button"
      >
        <WorkerTrackingDetailView titleId={DRAWER_TITLE_ID} state={detailState} />
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordId !== null}
        workerId={workerRecordId}
        onClose={() => setWorkerRecordId(null)}
        selectableSelector="tr.wx-trk-row, .wx-trk-card"
      />
    </AppShell>
  );
}

function WorkerTrackingCard({ item, onOpen }) {
  return (
    <div className="wx-trk-card" onClick={onOpen}>
      <div className="wx-trk-card-top">
        <div className="wx-trk-job-cell">
          <span className="wx-trk-avatar" aria-hidden="true">
            {monogram(item.workerName)}
          </span>
          <div>
            <strong>{item.workerName ?? "-"}</strong>
            <span>{item.workerCode ?? "-"}</span>
          </div>
        </div>
        <span className={`wx-badge wx-badge--${heartbeatBadgeVariant(item.heartbeatState)}`}>
          {HEARTBEAT_STATE_LABEL_TH[item.heartbeatState] ?? item.heartbeatState}
        </span>
      </div>
      <div className="wx-trk-card-grid">
        <div>
          <label>Assignment</label>
          <strong>{item.assignmentCode ?? "-"}</strong>
        </div>
        <div>
          <label>Heartbeat ล่าสุด</label>
          <strong className={item.lastHeartbeatAtUtc ? undefined : "wx-trk-reserved-text"}>
            {formatDateTimeTh(item.lastHeartbeatAtUtc) ?? "ยังไม่เคยส่ง"}
          </strong>
        </div>
        <div>
          <label>แบตเตอรี่ / เครือข่าย</label>
          <strong className={item.batteryLevel != null ? undefined : "wx-trk-reserved-text"}>
            {item.batteryLevel != null ? `${item.batteryLevel}%` : "ไม่มีข้อมูล"} · {item.networkType ?? "ไม่ทราบ"}
          </strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-trk-row-action wx-trk-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function WorkerTrackingDetailView({ titleId, state }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-trk-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const isMissing = detail.heartbeatState === "Missing";
  const hasCoordinates = detail.latitude != null && detail.longitude != null;

  return (
    <div className="wx-trk-drawer">
      {/* A worker opened from any screen reads the same as on Worker Profiles
          (components/RecordDrawerHeader.jsx). */}
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.workerName}
        title={detail.workerName ?? "-"}
        subtitle={detail.workerCode ?? "-"}
        badges={[
          { label: `overallStatus: ${detail.overallStatus ?? "-"}`, variant: "info" },
          {
            label: HEARTBEAT_STATE_LABEL_TH[detail.heartbeatState] ?? detail.heartbeatState,
            variant: heartbeatBadgeVariant(detail.heartbeatState),
          },
          { label: "ไม่ใช่ Footstep History", variant: "danger" },
        ]}
      />

      {/* Clicking a worker row anywhere in the app opens the same detailed
          worker profile the Agent portal shows. */}
      <WorkerProfileDetailedSection workerId={detail.workerId} />

      <section className="wx-trk-dsec">
        <h3>ข้อมูลแรงงาน</h3>
        <div className="wx-trk-kv-row">
          <span>Assignment</span>
          <strong className="wx-trk-code">{detail.assignmentCode ?? "-"}</strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>Agent</span>
          <strong>{detail.agentName ?? "-"}</strong>
        </div>
      </section>

      {isMissing && (
        <section className="wx-trk-dsec">
          <div className="wx-trk-abnormal-block">
            <div className="wx-trk-abnormal-head">
              <span className="wx-trk-abnormal-dot" aria-hidden="true" />
              <strong>{MISSING_HEARTBEAT_TITLE}</strong>
            </div>
            <p>{MISSING_HEARTBEAT_NOTE}</p>
          </div>
        </section>
      )}

      <section className="wx-trk-dsec">
        <h3>Heartbeat / ตำแหน่งล่าสุด</h3>
        <div className="wx-trk-kv-row">
          <span>สัญญาณล่าสุด</span>
          <strong className={detail.lastHeartbeatAtUtc ? undefined : "wx-trk-reserved-text"}>
            {formatDateTimeTh(detail.lastHeartbeatAtUtc) ?? "ยังไม่เคยส่ง heartbeat"}
          </strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>พิกัดล่าสุด</span>
          <strong className={detail.lastLocationAtUtc ? undefined : "wx-trk-reserved-text"}>
            {formatDateTimeTh(detail.lastLocationAtUtc) ?? "ยังไม่มีข้อมูลตำแหน่ง"}
          </strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>ความแม่นยำ (เมตร)</span>
          <strong className={detail.accuracyMeters != null ? undefined : "wx-trk-reserved-text"}>
            {detail.accuracyMeters != null ? `${detail.accuracyMeters} ม.` : "ไม่มีข้อมูล"}
          </strong>
        </div>

        {hasCoordinates ? (
          <div className="wx-trk-coord-box">
            <span className="wx-trk-coord-box-label">ตำแหน่งล่าสุด (ตัวเลขเท่านั้น ไม่มีแผนที่)</span>
            <div className="wx-trk-coord-pair">
              <div>
                <span>Latitude</span>
                <strong>{detail.latitude}</strong>
              </div>
              <div>
                <span>Longitude</span>
                <strong>{detail.longitude}</strong>
              </div>
            </div>
          </div>
        ) : (
          <p className="wx-trk-note-muted wx-trk-note-muted--left">ตำแหน่งล่าสุดยังไม่พร้อมแสดงบนแผนที่</p>
        )}
      </section>

      <section className="wx-trk-dsec">
        <h3>อุปกรณ์</h3>
        <div className="wx-trk-kv-row">
          <span>แบตเตอรี่</span>
          <strong className={detail.batteryLevel != null ? undefined : "wx-trk-reserved-text"}>
            {detail.batteryLevel != null ? `${detail.batteryLevel}%` : "ไม่มีข้อมูล"}
          </strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>ประเภทเครือข่าย</span>
          <strong className={detail.networkType ? undefined : "wx-trk-reserved-text"}>{detail.networkType ?? "ไม่ทราบ"}</strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>เวอร์ชันแอป</span>
          <strong className={detail.appVersion ? undefined : "wx-trk-reserved-text"}>{detail.appVersion ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-trk-kv-row">
          <span>สถานะการผูกเครื่อง</span>
          <strong>
            <span className="wx-badge wx-badge--info">{detail.deviceBindingStatus ?? "ไม่ระบุ"}</span>
          </strong>
        </div>
      </section>

      <section className="wx-trk-dsec">
        <h3>ขั้นตอนถัดไป (Next Actions)</h3>
        {/* A blocked row naming the canViewFootstepHistory flag stood here,
            inside an otherwise empty list. It printed a backend permission name
            at an operator, and the real link to the Footstep History screen is
            immediately below - so the list held nothing the sentence and the
            link do not already say. */}
        <p className="wx-trk-note-muted wx-trk-note-muted--left">{nextActions.footstepReservedReason || FOOTSTEP_RESERVED_REASON}</p>

        {/* A real, separate cross-link to the Footstep History screen
            (/backoffice/tracking/footsteps, P115B) - independent of the
            canViewFootstepHistory flag above. FootstepHistoryPage has no
            useSearchParams/query-param support to prefill workerId/from/to,
            so this opens the general screen only; its own purpose/reason
            gate and audit logging stay fully intact - this link never
            queries Footstep History on the operator's behalf. */}
        <Link to="/backoffice/tracking/footsteps" className="wx-button wx-button--ghost wx-trk-footstep-link">
          {FOOTSTEP_CROSSLINK_TEXT}
        </Link>
        <p className="wx-trk-note-muted wx-trk-note-muted--left">{FOOTSTEP_CROSSLINK_NOTE}</p>
      </section>
    </div>
  );
}
